/* NetworkDiscoveryGridViewRenderer.js */

"use strict";

const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class NetworkDiscoveryExplorerViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("network-discovery-grid-view-gl");
        this._logger = this._model.logger;
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );
        // this._model.gameEditor.on('ColumnSettingsChange', this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this));
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._planEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._planEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._planEntityGrid.on("UserSorted", (this._onUserSorted = this._onUserSorted.bind(this)));
        this._planEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
        if (this._planEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        if (this._planEntityGrid) {
            this._planEntityGrid.removeAllListeners("UserSelectedRows");
            this._planEntityGrid.removeAllListeners("UserRowDoubleClicked");
            this._planEntityGrid.removeListener("UserSorted", this._onUserSorted);
            this._planEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
        }
    }

    _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;

        if (numSelected > 0) {
            this._model.gameEditor.enableAllActions();
        } else {
            this._model.gameEditor.disableAllActions();
            this._model.gameEditor.enableAction(Registrations.NETWORK_DISCOVERY_ACTION_CREATE);
            this._model.gameEditor.enableAction(Registrations.NETWORK_DISCOVERY_ACTION_IMPORT);
        }

        if (numSelected > 1) {
            this._model.gameEditor.disableAction(Registrations.NETWORK_DISCOVERY_ACTION_CLONE);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            _.isUndefined(this._planEntityGrid)
                ? Promise.resolve()
                : this._planEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._planEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "PlanGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this._model.whenModelIsReady().then(() => {
            this._planEntityGrid = new EntityGrid({
                gridKey: this.model._gridKey,
                logger: this._logger,
                singleSelect: false,
                selectedRows: _.get(this._model.getSelectedPlanIds(), "selectedRows", []),
                columns: this._model.columnConfig,
                pagingFeed: this._model.pagingFeed,
                showHeader: true,
                showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                    "game-editor-view-actions-gl left-container"
                ),
                columnSettingsWidgetContainer:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                filterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                advancedFilterComponent:
                    this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                modalUserFeedback: this._model._modalUserFeedback,
                userGridData: this._model._userGridData,
            });

            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._planEntityGrid.renderer.rootElement);
            }
            this._renderAvailableActions();
            this._onFeedRefreshView();
            this._registerListeners();

            if (
                this._model._gameEditor._openEditorFromUrl &&
                (this._model._gameEditor._openEditorFromUrl.open ||
                    !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds))
            ) {
                _.get(this._model._gameEditor._openEditorFromUrl, "selectedIds", []).forEach(
                    (planid) => {
                        let Plan = this._model.pagingFeed.getNetworkDiscoveryEntityById(planid);
                        this._openEditor(Plan, planid);
                    }
                );
            }

            if (this._model._gameEditor._filterFromUrl) {
                this._planEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
            }
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._planEntityGrid.getSelectedRows();
    }

    restartGrid() {
        //this._unregisterGridListeners();
        return this._planEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._rootElem.appendChild(this._planEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message;
        switch (this._model._solutionName) {
            case "device_manager":
                message = {
                    message: "Create a new plan",
                    icon: "networkdiscovery.add_network_discovery",
                    action: Registrations.NETWORK_DISCOVERY_ACTION_CREATE,
                };
                break;
        }
        return this._model.gameEditor
            .showDefaultMessageView(message)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "PlanListViewerRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "PlanListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._planEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._planEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        // this._renderGridRows();
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        let Plan = this._model.pagingFeed.getNetworkDiscoveryEntityById(obj.selectedRow);

        this._openEditor(Plan, obj.selectedRow);
    }

    _openEditor(Plan, planId) {
        if (Plan.lastAuditBy) {
            // Config Drift
            this._model.openPlanInstanceEditor(
                planId,
                Registrations.NETWORK_DISCOVERY_EDITOR_VIEW_RESULTS
            );
        } else {
            // Default DIE view if neither in Config Drift nor in Plan Manager
            this._model.openPlanInstanceEditor(
                planId,
                Registrations.NETWORK_DISCOVERY_EDITOR_VIEW_DETAILS
            );
        }
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();

        if (totalEntities === 0 && _.isEmpty(filterData)) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = NetworkDiscoveryExplorerViewRenderer;
